"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { RomaReserveSheet } from "@/components/roma/roma-reserve-sheet";
import type { RomaReserveLocation } from "@/components/roma/roma-types";

type RomaReserveSheetContextValue = {
  enabled: boolean;
  locations: readonly RomaReserveLocation[];
  openSheet: () => void;
};

const RomaReserveSheetContext =
  createContext<RomaReserveSheetContextValue | null>(null);

export function RomaReserveSheetProvider({
  locations,
  children,
}: {
  locations: readonly RomaReserveLocation[];
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const enabled = locations.length > 0;
  const openSheet = useCallback(() => {
    if (enabled) {
      setOpen(true);
    }
  }, [enabled]);
  const value = useMemo(
    () => ({
      enabled,
      locations,
      openSheet,
    }),
    [enabled, locations, openSheet],
  );

  return (
    <RomaReserveSheetContext.Provider value={value}>
      {children}
      {enabled ? (
        <RomaReserveSheet
          locations={locations}
          open={open}
          onOpenChange={setOpen}
        />
      ) : null}
    </RomaReserveSheetContext.Provider>
  );
}

export function useRomaReserveSheet() {
  return useContext(RomaReserveSheetContext);
}
